'use client'
import { type FC, useMemo } from 'react'
import sanitizeHtml, { defaults, type Transformer, type IOptions } from 'sanitize-html'
import { normalizeHtmlNbsp, restoreEmptyHtmlLines } from 'shared-utils'

interface ParsedHtmlProps {
  html: string
}

/** @see https://www.npmjs.com/package/sanitize-html */
const htmlSanitizerOptions: IOptions = {
  allowVulnerableTags: true,
  allowedTags: defaults.allowedTags.concat(['a', 'img', 'iframe', 'video', 'source', 'style']),
  allowedClasses: {
    '*': ['*']
  },
  allowedAttributes: {
    ...defaults.allowedAttributes,
    '*': ['style', 'id'],
    a: ['href', 'rel', 'target'],
    iframe: ['src', 'style', 'height', 'width', 'title', 'loading', 'data-*'],
    video: [
      'style',
      'controls',
      'autoplay',
      'muted',
      'playsinline',
      'loop',
      'poster',
      'width',
      'height',
      'preload'
    ],
    source: ['src', 'type', 'media']
  },
  allowedIframeHostnames: ['widget.reco.se', 'www.youtube.com', 'player.vimeo.com'],
  transformTags: {
    // add lazy loading for the iframe by default
    iframe: ((tagName, attrs) => {
      return {
        tagName,
        attribs: {
          ...attrs,
          loading: attrs.loading ?? 'lazy'
        }
      }
    }) as Transformer
  }
}

/** Sanitize an html string and return a React component */
const ParsedHtml: FC<ParsedHtmlProps> = ({ html }) => {
  // content stored before the editor stopped escaping spaces still has to be made wrappable, and
  // content stored before it kept blank lines still has to get its `<br>`s back
  const parsedHtml = useMemo(
    () => sanitizeHtml(restoreEmptyHtmlLines(normalizeHtmlNbsp(html)), htmlSanitizerOptions),
    [html]
  )

  return <span dangerouslySetInnerHTML={{ __html: parsedHtml }} />
}

export default ParsedHtml
